<template>
  <!-- basic modal -->
  <b-modal
    id="create-gig-modal"
    size="xl"
    cancel-variant="outline-secondary"
    :title="isEditing ? $t('gig_page.edit_gig') : $t('gig_page.create_gig')"
    :no-close-on-backdrop="true"
    :cancel-title="$t('cancel')"
    :ok-title="isEditing ? $t('save_and_publish') : $t('create')"
    @show="onModalOpen"
    @hidden="resetModal"
    @ok="handleCreate"
  >
    <validation-observer ref="rules">
      <b-form
        ref="form"
        @submit.stop.prevent="handleSubmit"
      >
        <b-row>
          <b-col
            v-if="!editGig"
            cols="12"
          >
            <b-row>
              <!-- section info -->
              <b-col cols="12">
                <b-form-group
                  :label="$t('gig_page.local_service_area')"
                  label-for="section"
                >
                  <validation-provider
                    #default="{ errors }"
                    :name="$t('gig_page.local_service_area')"
                    rules="required"
                  >
                    <vue-autosuggest
                      v-model="areaName"
                      :suggestions="filteredOptions"
                      :get-suggestion-value="getSuggestionValue"
                      :limit="10"
                      :input-props="{
                        id: 'autosuggest__input',
                        class: 'form-control',
                        placeholder: $t('gig_page.location_placeholder'),
                        style: 'text-transform: capitalize',
                      }"
                      @selected="onSelected"
                      @input="onInputChange"
                    >
                      <template slot-scope="{ suggestion }">
                        <span
                          class="my-suggestion-item"
                          style="text-transform: capitalize"
                        >
                          {{ suggestion.item.name }}
                        </span><br>
                        <small class="my-suggestion-item">{{ suggestion.item.location.address.streetAddress }} - {{ suggestion.item.location.address.city }}</small>
                      </template>
                    </vue-autosuggest>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
            <b-col
              v-if="processer.role === 'admin' || appConfig.type === 'ow-admin'"
              cols="6"
            >
              <b-row
                class="boosted-row"
              >
                <b-col cols="2.5">
                  <b-form-checkbox
                    v-model="isTentative"
                    :checked="isTentative"
                    :checked-value="true"
                    :unchecked-value="false"
                    name="check-button"
                    style="margin-bottom: 5px;"
                  >
                    Alustava tilaus
                  </b-form-checkbox>
                  <b-form-checkbox
                    :checked="isAssignedToAll"
                    name="check-button"
                    @change="onAssignToAll"
                  >
                    {{ $t('assign_user_all') }}
                  </b-form-checkbox>
                </b-col>
                <b-col cols="5">
                  <b-form-group
                    v-if="isAssignedToAll"
                    :label="$t('employee')"
                    label-for="employee"
                    style="margin-left: 15px"
                  >
                    <vue-autosuggest
                      id="selectedEmployeeName"
                      v-model="selectedEmployeeName"
                      :suggestions="employeeOptions"
                      :get-suggestion-value="getEmployeeSuggestionValue"
                      :limit="10"
                      :input-props="{
                        id: 'autosuggest__input',
                        class: 'form-control',
                      }"
                      @input="fetchUsers"
                      @selected="onSelectedEmployee"
                    >
                      <template slot-scope="{ suggestion }">
                        <span
                          class="my-suggestion-item"
                          style="text-transform: capitalize"
                        >
                          {{ suggestion.item.label }}
                        </span>
                        <br>
                        <small v-if="appConfig.type === 'hw-admin'">{{ `${suggestion.item.value.profession}, ${suggestion.item.value.love ? 'luvallinen' : 'luvaton'}` }}</small>
                      </template>
                    </vue-autosuggest>
                  </b-form-group>
                  <b-form-invalid-feedback
                    v-if="isDoubleBooking && selectedEmployee"
                    class="my-1"
                    :state="false"
                    style="color: red; margin-left: 15px;"
                  >
                    {{ $t('shifts_calendar.double_shift_warning') }}
                  </b-form-invalid-feedback>
                </b-col>
              </b-row>
            </b-col>
            <b-row class="boosted-row" style="margin-top: 20px;">
              <b-col>
                <b-form-group
                  label="Vuorot erääntyy"
                >
                  <flat-pickr
                    v-model="form.expDate"
                    class="form-control"
                    :config="{ enableTime: true, time_24hr: true, minDate: new Date(), minuteIncrement: 1 }"
                    style="width: 15%"
                  />
                </b-form-group>
                <b-button @click="form.expDate = null">Tyhjennä</b-button>
              </b-col>
            </b-row>
          </b-col>
          <b-col v-if="!editGig">
            <hr>
          </b-col>
          <!-- contract info -->
          <app-collapse style="width: 100%; margin: 0 auto; padding: 0 10px">
            <app-collapse-item
              :title="$t('gig_page.selected_area') + ': ' + areaName"
              style="text-transform: capitalize"
            >
              <b-col
                v-if="user.role !== 'customer'"
                cols="12"
              >
                <b-form-group
                  :label="$t('gig_page.client_email')"
                  label-for="client-email"
                >
                  <validation-provider
                    #default="{ errors }"
                    :name="$t('gig_page.client_email')"
                    rules="required"
                  >
                    <v-select
                      v-model="form.order.emails"
                      multiple
                      taggable
                      :no-drop="true"
                      style="text-transform: lowercase"
                    />
                    <small class="text-danger">
                      {{ errors[0] ? $t('gig_page.client_email_warning') : errors[0] }}
                    </small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col v-if="user.role != 'admin'">
                <hr>
              </b-col>
              <b-col cols="12">
                <b-row>
                  <!-- street address -->
                  <b-col md="6">
                    <b-form-group
                      :label="$t('gig_page.street_address')"
                      label-for="street-address"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('gig_page.street_address')"
                        rules="required"
                      >
                        <b-form-input
                          id="street-address"
                          v-model="form.location.address.streetAddress"
                          :state="errors.length > 0 ? false:null"
                          placeholder=""
                          :disabled="true"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <!-- city -->
                  <b-col cols="6">
                    <b-form-group
                      :label="$t('gig_page.city')"
                      label-for="city"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('gig_page.city')"
                        rules="required"
                      >
                        <b-form-input
                          id="city"
                          v-model="form.location.address.city"
                          :state="errors.length > 0 ? false:null"
                          placeholder=""
                          :disabled="true"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <!-- post code -->
                  <b-col cols="6">
                    <b-form-group
                      :label="$t('gig_page.postcode')"
                      label-for="postcode"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('gig_page.postcode')"
                        rules="required"
                      >
                        <b-form-input
                          id="postcode"
                          v-model="form.location.address.postcode"
                          :state="errors.length > 0 ? false:null"
                          type="number"
                          :disabled="true"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <!-- state -->
                  <b-col cols="6">
                    <b-form-group
                      :label="$t('gig_page.state')"
                      label-for="state"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="$t('gig_page.state')"
                        rules="required"
                      >
                        <v-select
                          v-model="form.location.address.state"
                          :state="errors.length > 0 ? false:null"
                          :label="$t('gig_page.state')"
                          :options="STATE_OPTIONS"
                          style="text-transform: capitalize"
                          :disabled="true"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-col>
              <b-col>
                <hr>
              </b-col>
              <b-col cols="12">
                <b-row>
                  <b-col cols="12">
                    <b-form-group
                      :label="$t('gig_page.contact_info')"
                    />
                  </b-col>
                  <b-col cols="6">
                    <b-form-group>
                      <b-form-input
                        id="contact_1"
                        v-model="form.location.contact[0]"
                        :placeholder="$t('phone_placeholder')"
                        :disabled="true"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group>
                      <b-form-input
                        id="contact_2"
                        v-model="form.location.contact[1]"
                        :placeholder="$t('contact_person_placeholder')"
                        :disabled="true"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group>
                      <b-form-input
                        id="contact_3"
                        v-model="form.location.contact[2]"
                        :placeholder="$t('email_placeholder')"
                        :disabled="true"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group>
                      <b-form-input
                        id="contact_4"
                        v-model="form.location.contact[3]"
                        placeholder=""
                        :disabled="true"
                      />
                    </b-form-group>
                  </b-col>
                  <!-- abscence info -->
                  <b-col cols="12">
                    <b-form-group
                      label-for="abscence"
                      :label="$t('gig_page.abscence_info')"
                    >
                      <b-form-textarea
                        id="abscence"
                        v-model="form.location.abscenceInfo"
                        :placeholder="$t('absence_info_placeholder')"
                        rows="3"
                        no-resize
                        :disabled="true"
                      />
                    </b-form-group>
                  </b-col>
                  <!-- location details -->
                  <b-col cols="12">
                    <b-form-group
                      :label="$t('gig_page.location_details')"
                      label-for="location-details"
                    >
                      <b-form-textarea
                        id="textarea-no-resize"
                        v-model="form.location.locationDetails"
                        :placeholder="$t('entrance_placeholder')"
                        rows="3"
                        no-resize
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-col>
              <b-col>
                <hr>
              </b-col>
            </app-collapse-item>
          </app-collapse>
          <b-col>
            <hr>
          </b-col>
          <b-col>
            <hr>
          </b-col>
          <b-col cols="12">
            <b-col
              v-if="processer.role === 'admin'"
              cols="6"
            >
              <b-row
                class="boosted-row"
              >
                <b-col cols="2.5">
                  <b-form-checkbox
                    :checked="isBoosted"
                    name="check-button"
                    @change="onToggleBoosted"
                  >
                    {{ $t('boost_option_title') }}
                  </b-form-checkbox>
                </b-col>
                <b-col cols="5">
                  <b-form-group
                    v-if="isBoosted"
                    :label="$t('gig_page.boost_type_label')"
                    label-for="boost_type"
                    style="margin-left: 15px"
                  >
                    <v-select
                      id="boost_type"
                      v-model="selectedBoostedOption"
                      :options="SHIFT_BOOST_TYPE_OPTIONS"
                      :clearable="false"
                      inline
                      @input="onSelectedBoostType(selectedBoostedOption)"
                    />
                  </b-form-group>
                </b-col>
                <b-col cols="auto">
                  <b-form-group
                    v-if="isBoosted && selectedBoostedOption.value !== SHIFT_BOOST_TYPES.quick.value"
                    :label="$t('gig_page.boost_value_label')"
                    label-for="boost_value"
                    style="margin-left: 10px"
                  >
                    <validation-provider
                      #default="{ errors }"
                      :name="$t('gig_page.boost_value_label')"
                      rules="required"
                    >
                      <b-form-input
                        id="boost_value"
                        v-model="selectedBoostedValue"
                        type="number"
                        :placeholder="$t('gig_page.boost_value_placeholder')"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-col>
            <b-col v-if="processer.role === 'admin'">
              <hr>
            </b-col>
            <b-col
              cols="12 "
            >
              <b-form-group
                :label="$t('shift_text_input')"
              />
              <b-form-textarea
                id="textarea-no-resize"
                v-model="text"
                :placeholder="$t('shift_text_input_example')"
                rows="10"
                no-resize
                class="my-1"
                debounce="2000"
              />
            </b-col>
            <b-col cols="12">
              <b-form-group
                :label="$t('gig_page.shifts')"
              />
            </b-col>
            <b-col cols="12">
              <b-row
                v-for="(item, index) in shifts"
                :id="item.id"
                :key="index"
                ref="row"
                class="shift-item"
              >
                <b-col md="5">
                  <b-row>
                    <b-col md="6">
                      <b-form-group
                        :label="$t('gig_page.start')"
                        label-for="start"
                      >
                        <validation-provider
                          #default="{ errors }"
                          :name="$t('gig_page.start')"
                          rules="required"
                        >
                          <flat-pickr
                            v-model="item.startStr"
                            class="form-control"
                            :state="errors.length > 0 ? false:null"
                            :config="{ enableTime: true, time_24hr: true, defaultDate: getDefaultTime(7, 0), minuteIncrement: 1, static: true }"
                            style="width: 18vw"
                            @on-change="(date) => { setDate(item, date, 'start') }"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <b-col
                      md="6"
                      class="shift-end"
                    >
                      <b-form-group
                        :label="$t('gig_page.end', { msg: 'End' })"
                        label-for="end"
                      >
                        <validation-provider
                          #default="{ errors }"
                          :name="$t('gig_page.end', { msg: 'End' })"
                          rules="required"
                        >
                          <flat-pickr
                            v-model="item.endStr"
                            class="form-control"
                            :state="errors.length > 0 ? false:null"
                            :config="{ enableTime: true, minDate: item.start, time_24hr: true, defaultDate: getDefaultTime(15, 0), minuteIncrement: 1, static: true }"
                            style="width: 19vw"
                            @on-change="(date) => { setDate(item, date, 'end') }"
                          />
                          <small
                            v-if="errors[0]"
                            class="text-danger"
                          >
                            {{ errors[0] }}
                          </small>
                          <small
                            v-else-if="item.startStr && item.endStr && (item.startStr.slice(0, 10) !== item.endStr.slice(0, 10))"
                            class="text-warning"
                          >
                            {{ $t('gig_page.shift_diff_date_warning') }}
                            <feather-icon
                              icon="AlertTriangleIcon"
                              class="margin-left:5px;"
                              size="12"
                            />
                          </small>

                        </validation-provider>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>
                <b-col
                  md="5"
                  class="shift-requirements"
                >
                  <b-row>
                    <b-col
                      md="6"
                    >
                      <b-form-group
                        :label="$t('gig_page.role_type')"
                        label-for="role_type"
                      >
                        <validation-provider
                          #default="{ errors }"
                          :name="$t('gig_page.role_type')"
                          rules="required"
                        >
                          <v-select
                            v-model="item.roleType"
                            :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
                            :label="$t('gig_page.role_type')"
                            style="text-transform: capitalize"
                            :clearable="false"
                            :options="typeReqOptions"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                      </b-form-group>
                    </b-col>
                    <!-- Requirements -->
                    <b-col
                      md="6"
                      class="shift-requirements-col"
                    >
                      <b-form-group
                        :label="$t('gig_page.requirements')"
                        label-for="requirements"
                      >
                        <v-select
                          v-model="item.requirements"
                          :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
                          multiple
                          :label="$t('gig_page.requirements')"
                          style="text-transform: capitalize"
                          :options="appConfig.type === 'ow-admin' ? oiwaRequirements : locale === 'de' ? SHIFT_REQUIREMENTS_OPTIONS_GERMANY : SHIFT_REQUIREMENTS_OPTIONS_FINLAND"
                          @input="onEditRequirements(item)"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>
                <b-col
                  class="shift-actions"
                >
                  <b-row>
                    <b-button
                      v-ripple.400="'rgba(234, 84, 85, 0.15)'"
                      size="sm"
                      variant="outline-secondary"
                      class="mt-0 mt-md-2"
                      @click="copy(item, index)"
                    >
                      {{ $t('copy') }}
                    </b-button>
                    <b-button
                      v-if="isAssignedToAll || !item.assignedTo || item.assignedTo.length < 1"
                      v-ripple.400="'rgba(234, 84, 85, 0.15)'"
                      size="sm"
                      variant="outline-danger"
                      class="mt-0 mt-md-2 ml-1"
                      @click="removeShift(index)"
                    >
                      <feather-icon
                        icon="XIcon"
                        size="18"
                      />
                    </b-button>
                  </b-row>
                </b-col>
                <b-col v-if="processer.role === 'admin'" cols="6">
                  <b-row style="align-items: center; margin-left: 0px">
                    <b-form-invalid-feedback
                      :state="showWarning(item)"
                      style="color: red"
                    >
                      {{ $t('shifts_calendar.double_shift_warning') }}
                    </b-form-invalid-feedback>

                    <p v-if="salaryData && item.roleType && user.role === 'admin'" style="font-size: 1.2rem; font-weight: 500; margin-top: 10px;">{{ `${getSalaryData(item)} €/h (ei sis. lomakorvausta ja lisiä)` }}</p>
                  </b-row>
                </b-col>
                <b-col cols="12">
                  <hr>
                </b-col>
              </b-row>
            </b-col>
            <!-- submit and reset -->
            <b-col cols="12">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="button"
                variant="outline-primary"
                class="mr-1"
                @click="addShift"
              >
                {{ $t('gig_page.add_shift') }}
              </b-button>
            </b-col>
          </b-col></b-row>
      </b-form>
    </validation-observer>
    <template #modal-footer="{ cancel }">
      <div style="display: flex; flex-direction: column;">
        <div style="display: flex; flex-direction: row; justify-content: flex-end;">
          <b-button
            variant="outline-secondary"
            style="margin-right: 15px;"
            @click="cancel()"
          >
            {{ $t('cancel') }}
          </b-button>
          <b-button
            variant="info"
            :disabled="isDoubleBooking"
            @click.prevent="handleCreate(true, false)"
          >
            {{ $t('gig_page.saveDraft') }}
          </b-button>
          <b-button
            v-if="shifts.some(shift => !shift.assignedTo) && assignedArea && (!assignedArea.isJoiku || (assignedArea.isJoiku && assignedArea.isPublicSearchAllowed))"
            variant="primary"
            :disabled="isDoubleBooking || isContractExpired || subscriptionLimitExceeded"
            class="ml-1"
            @click.prevent="handleCreate(false, false)"
          >
            {{ $t('publish') }}
          </b-button>
          <b-button
            v-if="shifts.some(shift => shift.assignedTo) && assignedArea && (!assignedArea.isJoiku || (assignedArea.isJoiku && assignedArea.isPublicSearchAllowed))"
            variant="warning"
            :disabled="isDoubleBooking || isContractExpired || subscriptionLimitExceeded"
            class="ml-1"
            @click.prevent="handleCreate(true, false)"
          >
            Lähetä Toimintakeskukseen
          </b-button>
          <b-button
            v-if="shifts.some(shift => shift.assignedTo) && assignedArea && (!assignedArea.isJoiku || (assignedArea.isJoiku && assignedArea.isPublicSearchAllowed))"
            variant="primary"
            :disabled="isDoubleBooking || isContractExpired || subscriptionLimitExceeded"
            class="ml-1"
            @click.prevent="handleCreate(true, true)"
          >
            Vahvista
          </b-button>
        </div>
        <p
          v-if="assignedArea && isContractExpired"
          style="margin-top: 15px; color: red"
        >
          Alueen sopimus on vanhentunut, päivitä sopimus jotta voit julkaista vuoroja!
        </p>
        <p
          v-if="assignedArea && subscriptionLimitExceeded"
          style="margin-top: 15px; color: red"
        >
          Olet julkaissut maksimimäärän vuoroja tässä kuussa, päivitä tilaus jotta voit julkaista lisää vuoroja!
        </p>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BModal, BRow, BCol, BFormGroup, BFormInput, BForm, BButton,
  BFormTextarea, BFormCheckbox, BFormInvalidFeedback
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import Ripple from 'vue-ripple-directive'
import { required } from '@validations'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'
import { VueAutosuggest } from 'vue-autosuggest'
import AppCollapse from '@hw-ui/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@hw-ui/components/app-collapse/AppCollapseItem.vue'
import getShiftSuggestions from '@hw-core/shift-extractor/'
import { SHIFT_REQUIREMENTS_OPTIONS_FINLAND, SHIFT_REQUIREMENTS_OPTIONS_GERMANY, SHIFT_REQUIREMENT_OPTIONS_OIWA, STATE_OPTIONS } from '@/shared/constants/options'
import { SHIFT_UPDATE_STATES, SHIFT_BOOST_TYPES, SHIFT_BOOST_TYPE_OPTIONS, SHIFT_STATUSES, SHIFT_WORK_TYPE, SHIFT_DEFAULT_VALUES } from '@/shared/constants/shifts'
import { TOAST_VARIABLES } from '@hw-ui/components/toastification/constant'
import { GIG_STATUSES } from '@/shared/constants/gigs'
import hwToast from '@hw-ui/components/toastification/hw-toast'
import gigPublisher from '../service/gig-publisher'

function timeConflict(time, start, end, previousAreaId, currAreaId) {
  return previousAreaId === currAreaId
    ? start < time && time < end
    : start <= time && time <= end
}

const onConvertToDateObject = (date, time) => {
  const day = date.date
  const { month } = date
  const { year } = date
  const hour = time.hours
  const minute = time.minutes
  const timeString = `${year}-${month}-${day} ${hour}:${minute}`
  return new Date(timeString)
}
    
const onConvertDateToString = date => {
  const convertedDate = new Date(date)
  return (`${
    convertedDate.getFullYear().toString().padStart(4, '0')}-${
    (convertedDate.getMonth() + 1).toString().padStart(2, '0')}-${
    convertedDate.getDate().toString().padStart(2, '0')} ${
    convertedDate.getHours().toString().padStart(2, '0')}:${
    convertedDate.getMinutes().toString().padStart(2, '0')}`
  )
}

const onConvertDate = date => {
  const convertedDate = new Date(date)
  return (`${
    convertedDate.getFullYear().toString().padStart(4, '0')}-${
    (convertedDate.getMonth() + 1).toString().padStart(2, '0')}-${
    convertedDate.getDate().toString().padStart(2, '0')} ${
    convertedDate.getHours().toString().padStart(2, '0')}:${
    convertedDate.getMinutes().toString().padStart(2, '0')}`
  )
}

export default {
  components: {
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BFormCheckbox,
    BButton,
    vSelect,
    BFormTextarea,
    flatPickr,
    ValidationProvider,
    ValidationObserver,
    VueAutosuggest,
    AppCollapse,
    AppCollapseItem,
    BFormInvalidFeedback
  },
  directives: {
    Ripple,
  },
  props: {
    editGig: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      subscriptionLimitExceeded: false,
      oiwaRequirements: this.$store.state.appAccount.selectedAccount.value.requirements,
      required,
      text: '',
      template: null,
      contract: null,
      templateOptions: [],
      employeeOptions: [],
      contractOptions: [],
      datasuggest: [],
      filteredOptions: [],
      assignedArea: null,
      limit: 10,
      selected: null,
      areaName: '',
      isContractExpired: false,
      locale: localStorage.getItem('locale'),
      isEditing: false,
      isAssignedToAll: false,
      isTentative: false,
      fetchingLatLon: false,
      processer: {},
      form: {
        isTemplate: false,
        isPublished: false,
        startDate: null,
        expDate: null,
        category: null,
        accountId: null,
        tags: [],
        order: {
          emails: [],
        },
        location: {
          address: {},
          contact: [],
          abscenceInfo: '',
          gps: {
            lat: null,
            lon: null,
          },
        },
      },
      SHIFT_REQUIREMENTS_OPTIONS_FINLAND,
      SHIFT_REQUIREMENTS_OPTIONS_GERMANY,
      SHIFT_REQUIREMENT_OPTIONS_OIWA,
      SHIFT_BOOST_TYPES,
      SHIFT_DEFAULT_VALUES,
      shifts: [{
        startStr: '',
        endStr: '',
        start: null,
        end: null,
        assignedTo: '',
        status: SHIFT_STATUSES.open,
        reserved: false,
        tentative: false,
        requirements: [],
        roleType: this.$store.state.app.appConfig.type === 'hw-admin'
          ? SHIFT_DEFAULT_VALUES.roleType
          : null,
        workDayType: SHIFT_WORK_TYPE.work,
        updates: [],
        confirmationRequest: '',
        verificationRequest: '',
        finalConfirmation: { confirmed: false },
      }],
      STATE_OPTIONS,
      updateStates: [],
      countSetDate: 0,
      isBoosted: false,
      selectedBoostedOption: SHIFT_BOOST_TYPES.quick,
      selectedEmployee: null,
      selectedEmployeeName: '',
      SHIFT_BOOST_TYPE_OPTIONS,
      selectedBoostedValue: '',
      SHIFT_UPDATE_STATES,
      TOAST_VARIABLES,
      SHIFT_STATUSES,
      SHIFT_WORK_TYPE,
      isDoubleBooking: false,
      userUpcomingShifts: null,
      salaryData: null,
    }
  },
  computed: {
    selectedAccount() {
      return this.$store.state.appAccount.selectedAccount
    },
    user() {
      return this.$store.state.appAuth.authUser ? this.$store.state.appAuth.authUser : {}
    },
    appConfig() {
      return this.$store.state.app.appConfig
    },
    typeReqOptions() {
      // TODO: HACK FOR OIWA, TO HIDE OIWA ROLES FROM HOIWA CLIENTS AND VICE VERSA CAN BE REMOVED LATER
      const {roles, germanRoles, selectedAccount} = this.$store.state.appAccount
      if (selectedAccount && selectedAccount.value.category === 'oiwa') {
        return roles.filter(option => option.oiwaOnly).map(role => role.label)
      }
      // eslint-disable-next-line no-nested-ternary
      return this.$store.state.app.appConfig.type === 'ow-admin'
        ? this.$store.state.appAccount.selectedAccount.value.professions
        : this.$i18n.locale === 'de'
          ? germanRoles.filter(option => !option.oiwaOnly).map(role => role.label)
          : roles.filter(option => !option.oiwaOnly).map(role => role.label)
    },
    canWorkAs() {
      const {roles, germanRoles, selectedAccount} = this.$store.state.appAccount

      if (selectedAccount && selectedAccount.value.category === 'oiwa') {
        return roles.filter(option => option.oiwaOnly)
      }
      // eslint-disable-next-line no-nested-ternary
      return this.$store.state.app.appConfig.type === 'ow-admin'
        ? {}
        : this.$i18n.locale === 'de'
          ? germanRoles.filter(option => !option.oiwaOnly)
          : roles.filter(option => !option.oiwaOnly)
    },
  },
  watch: {
    async assignedArea(newVal) {
      if (newVal) {
        this.form.location = newVal.location
        this.contract = await this.$store.dispatch('appGigs/fetchContract', { contractId: newVal.contract })
      }
    },
    text(newVal) {
      let data = []
      if (newVal) {
        data = getShiftSuggestions(newVal, this.selectedAccount.value.professions || this.typeReqOptions, this.selectedAccount.value.requirements || this.SHIFT_REQUIREMENTS_OPTIONS_FINLAND)
      }
      const result = data.filter(item => (item.dates && item.start && item.end && item.role))
      this.createShifts(result)
    },
    isTentative(newVal) {
      this.shifts = this.shifts.map(shift => ({
        ...shift,
        tentative: newVal,
      }))
    },
  },
  beforeCreate() {
    this.$store.dispatch('appAuth/getCurrentUser')
      .then(res => {
        const user = JSON.parse(JSON.stringify(res))
        this.processer = {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
        }
      })
    this.$store.dispatch({
      type: 'appAuth/getAllEmployees',
      query: '',
    })
    .then(res => {
      this.employeeOptions = res
    })
  },
  methods: {
    getSalaryData(shift) {
      const keys = ['default'].concat(Object.keys(this.salaryData)).filter(key => this.canWorkAs.filter(item => item.label === shift.roleType)[0].canWorkAs?.includes(key))
      if (shift.requirements.includes('love')) {
        keys.push('love')
      }

      let result = this.salaryData.default
      keys.forEach(key => {
        result = this.salaryData[key] > result ? this.salaryData[key] : result
      })

      return result
    },
    showWarning(item) {
      let show = true
      if (item.assignedTo && this.userUpcomingShifts !== null) {
        // eslint-disable-next-line array-callback-return, consistent-return
        this.userUpcomingShifts.filter(shift => {
          if ((timeConflict(shift.start, item.start, item.end, shift.areaId, item.areaId) || timeConflict(shift.end, item.start, item.end, shift.areaId, item.areaId))) {
            show = false
            // eslint-disable-next-line max-len
          } if ((timeConflict(item.start, shift.start, shift.end, shift.areaId, item.areaId) || timeConflict(item.end, shift.start, shift.end, shift.areaId, item.areaId))) {
            show = false
          }
        })
      }

      return show
    },
    checkTimeConflict(assignedTo) {
      if (assignedTo) {
        const firstShiftStart = this.shifts[0].start
        this.$emit('fetch-user-upcoming-shifts', {
          assignedTo: assignedTo.value.id,
          firstShiftStart,
          callback: shifts => {
            this.isLoading = false
            this.isDoubleBooking = false
            this.userUpcomingShifts = shifts
            // eslint-disable-next-line no-restricted-syntax
            for (const item of this.shifts) {
              // eslint-disable-next-line array-callback-return, consistent-return
              shifts.filter(shift => {
                if ((timeConflict(shift.start, item.start, item.end, shift.areaId, item.areaId) || timeConflict(shift.end, item.start, item.end, shift.areaId, item.areaId))) {
                  this.isDoubleBooking = true
                  // eslint-disable-next-line max-len
                } if ((timeConflict(item.start, shift.start, shift.end, shift.areaId, item.areaId) || timeConflict(item.end, shift.start, shift.end, shift.areaId, item.areaId))) {
                  this.isDoubleBooking = true
                }
              })
            }
          },
        })
      } else {
        this.isDoubleBooking = false
      }
    },
    async checkSubscriptionLimits() {
      if (this.selectedAccount.value.subscription) {
        const {level} = this.selectedAccount.value.subscription

        // Get current gig shifts
        const currentGigShifts = this.shifts.length

        // Get this month's shifts
        const thisMonthShifts = await this.$store.dispatch('appGigs/fetchThisMonthShifts')

        this.subscriptionLimitExceeded = ((level + 1) * 25) < (currentGigShifts + thisMonthShifts)
      }
    },
    createShifts(suggestions) {
      this.shifts = []
      suggestions.forEach(s => {
        // Iterate dates
        for (let d = 0; d < s.dates.length; d += 1) {
          const date = s.dates[d]
          const startDate = onConvertToDateObject(date, s.start)
          const endDate = onConvertToDateObject(date, s.end)
          if(startDate > endDate) {
            endDate.setDate(endDate.getDate() + 1)
          }
          for (let i = 0; i < s.multipication[0]; i += 1) {
            const shift = {}
            shift.start = startDate
            shift.end = endDate
            shift.startStr = onConvertDateToString(shift.start)
            shift.endStr = onConvertDateToString(shift.end)
            shift.status = SHIFT_STATUSES.open
            shift.workDayType = SHIFT_WORK_TYPE.work
            shift.kilometrikorvaus = 0
            shift.requirements = s.requirements
            shift.reserved = this.isAssignedToAll
            shift.tentative = this.isTentative
            shift.roleType = s.role
            shift.finalConfirmation = { confirmed: false }
            shift.confirmationRequest = ''
            shift.verificationRequest = ''
            shift.assignedTo = this.isAssignedToAll ? this.selectedEmployee : ''
            shift.updates = [
              {
                state: SHIFT_UPDATE_STATES.created,
                processer: `${this.processer.firstName} ${this.processer.lastName}`,
                date: new Date(),
                content: ['Created Shift'],
              }]
            this.shifts.push(shift)
          }
        }
      })
    },
    fetchUsers(q) {
      if (q === '' || q === undefined) {
        this.selectedEmployee = null
        this.selectedEmployeeName = ''
        this.shifts = this.shifts.map(shift => ({
          ...shift,
          assignedTo: '',
        }))
        this.isDoubleBooking = false
        return
      }

      this.$store.dispatch({
        type: 'appAuth/getAllEmployees',
        query: q,
      }).then(res => {
          this.employeeOptions = [{ data: res }]
        })
    },
    setDate(item, date, slot) {
      if (this.isEditing) {
        this.countSetDate += 1
        if (this.countSetDate > 2) {
          const exist = this.updateStates.find(update => update.id === item.id)
          const index = this.updateStates.indexOf(exist)
          const updateObj = (index !== -1) ? this.updateStates[index] : {
            state: SHIFT_UPDATE_STATES.updated,
            processer: `${this.processer.firstName} ${this.processer.lastName}`,
            date: new Date(),
            content: {},
            id: item.id,
          }
          updateObj.content = { ...updateObj.content, [slot]: onConvertDate(date) }
          this.updateStates[index > -1 ? index : this.updateStates.length] = updateObj
        }
      }
      /* eslint-disable no-param-reassign */
      if (slot === 'start') {
        [item.start] = date
        if (item.start > item.end) {
          const dt = new Date(date)
          dt.setHours(dt.getHours() + 8)
          item.end = dt
          item.endStr = onConvertDate(dt)
        }
      } else {
        [item.end] = date
      }

      this.checkTimeConflict(this.selectedEmployee)
      this.checkSubscriptionLimits()
    },
    // TODO currently unused
    setLatLon() {
      this.fetchingLatLon = true
      const address = `${this.form.location.address.streetAddress} ${this.form.location.address.city}`
      this.$store.dispatch('appGigs/fetchLocationLatLon', { address })
        .then(response => {
          const gps = response.data.result
          this.form.location.gps.lat = gps.latitude
          this.form.location.gps.lon = gps.longitude
          this.fetchingLatLon = false
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Error',
              icon: TOAST_VARIABLES.icons.error,
              variant: TOAST_VARIABLES.variants.error,
              text: 'Could not get location. Please check street address.',
            },
          })
          this.fetchingLatLon = false
        })
    },
    getDefaultTime(hour, minute) {
      const today = new Date()
      return new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour, minute)
    },
    onInputChange(text) {
      if (text === '' || text === undefined) {
        this.isContractExpired = false
        this.salaryData = null
        return
      }
      this.$emit('search-location', {
        text: text.toLowerCase(),
        callback: result => {
          this.filteredOptions = [{ data: result }]
        },
      })
    },
    async onSelected(option) {
      if (option) {
        this.assignedArea = option.item
        this.areaName = option.item.name
        if (option.item.emails) {
          this.form.order.emails = option.item.emails
        }
        const contract = await this.$store.dispatch('appAuth/fetchContract', { contractId: option.item.contract })
        this.isContractExpired = !contract.end
          ? false
          : contract.end.toDate() < new Date()
        this.salaryData = contract.salary
      }
    },
    getSuggestionValue(option) {
      return option.item.name
    },
    getEmployeeSuggestionValue(option) {
      return `${option.item.label.split(' ')[0].slice(0,1).toUpperCase()}${option.item.label.split(' ')[0].slice(1)} ${option.item.label.split(' ')[1].slice(0,1).toUpperCase()}${option.item.label.split(' ')[1].slice(1)}`
    },
    onModalOpen() {
      if (this.editGig) {
        this.isEditing = true
        this.form = { ...this.editGig }
        if (!this.form.order) {
          this.form.order = { emails: [] }
        }
        this.isBoosted = this.form.boosted ? this.form.boosted : false
        this.selectedBoostedOption = SHIFT_BOOST_TYPES[this.form?.boostedOptions.boostType || SHIFT_BOOST_TYPES.quick.value]
        this.selectedBoostedValue = this.form.boostedOptions?.value || ''
      } else {
        this.checkSubscriptionLimits()

        this.shifts[0].start = this.getDefaultTime(7, 0)
        this.shifts[0].end = this.getDefaultTime(15, 0)
        this.shifts[0].startStr = onConvertDate(this.getDefaultTime(7, 0))
        this.shifts[0].endStr = onConvertDate(this.getDefaultTime(15, 0))
        this.shifts[0].createdBy = this.processer.role
        this.shifts[0].updates.push({
          state: SHIFT_UPDATE_STATES.created,
          processer: `${this.processer.firstName} ${this.processer.lastName}`,
          date: new Date(),
          content: ['Created Shift'],
        })
      }
      this.$emit('modal-open', {
        id: this.isEditing ? this.form.id : null,
        areaId: this.form.areaId,
        callback: async ({ shifts, assignedArea }) => {
          const userNames = []
          // eslint-disable-next-line no-restricted-syntax, no-unused-vars
          for (const shift of shifts) {

            if(shift.assignedTo && shift.assignedTo !== '') {
              /* eslint-disable no-await-in-loop */
              const name = await this.fetchEmployeeName(shift.assignedTo)
              userNames.push({
                name,
                id: shift.id,
              })
            }
          }
          const cloneShifts = shifts.map(shift => {
            const s = shift
            s.roleType = this.typeReqOptions.find(option => shift.requirements.includes(option))
            const requirements = shift.requirements.filter(req => req !== s.roleType)
            s.requirements = requirements
            if(s.assignedTo) {
              s.assignedTo = {
                label: userNames.find(obj => obj.id === s.id).name,
                value: {
                  name: userNames.find(obj => obj.id === s.id).name,
                  id: s.assignedTo,
                }
              }
            }
            return s
          })

          this.shifts = this.isEditing ? cloneShifts : this.shifts
          this.assignedArea = assignedArea
          this.areaName = this.assignedArea.name
        },
      })
    },
    async fetchEmployeeName(id) {
      const data = await this.$store.dispatch({
        type: 'appAuth/getEmployee',
        query: id,
      })
      return data
    },
    resetModal() { // TODO move to state
      this.template = null
      this.isEditing = false
      this.contract = null
      this.assignedArea = null
      this.areaName = ''
      this.text = ''
      this.isContractExpired = false
      this.salaryData = null
      this.fetchingLatLon = false
      this.isTentative = false
      this.form = {
        isTemplate: false,
        isPublished: false,
        startDate: null,
        expDate: null,
        category: null,
        accountId: this.selectedAccount.value.id,
        tags: [],
        order: {
          emails: [],
        },
        location: {
          address: {},
          contact: [],
          abscenceInfo: '',
          sectionInfo: '',
          gps: {
            lat: null,
            lon: null,
          },
        },
      }
      this.isBoosted = false
      this.selectedBoostedOption = SHIFT_BOOST_TYPES.quick
      this.selectedBoostedValue = ''
      this.isDoubleBooking = false
      this.isAssignedToAll = false
      this.selectedEmployee = null
      this.selectedEmployeeName = ''
      this.shifts = [
        {
          startStr: '',
          endStr: '',
          start: null,
          end: null,
          assignedTo: '',
          status: SHIFT_STATUSES.open,
          reserved: false,
          tentative: false,
          requirements: [],
          roleType: this.$store.state.app.appConfig.type === 'hw-admin'
            ? SHIFT_DEFAULT_VALUES.roleType
            : null,
          workDayType: SHIFT_WORK_TYPE.work,
          updates: [
            {
              state: SHIFT_UPDATE_STATES.created,
              processer: `${this.processer.firstName} ${this.processer.lastName}`,
              date: new Date(),
              content: ['Created Shift'],
            },
          ],
          confirmationRequest: '',
          verificationRequest: '',
          finalConfirmation: { confirmed: false },
        },
      ]
      this.$emit('modal-close')
    },
    // TODO currently unused
    checkFormValidity() {
      const valid = this.$refs.form.checkValidity()
      return valid
    },
    addShift() {
      this.$refs.rules.validate()
      this.shifts.push({
        startStr: onConvertDate(this.getDefaultTime(7, 0)),
        endStr: onConvertDate(this.getDefaultTime(15, 0)),
        start: this.getDefaultTime(7, 0),
        end: this.getDefaultTime(15, 0),
        assignedTo: this.isAssignedToAll ? this.selectedEmployee : '',
        workDayType: SHIFT_WORK_TYPE.work,
        roleType: SHIFT_DEFAULT_VALUES.roleType,
        requirements: [],
        finalConfirmation: { confirmed: false },
        confirmationRequest: '',
        verificationRequest: '',
        createdBy: this.processer.role,
        updates: [
          {
            state: SHIFT_UPDATE_STATES.created,
            processer: `${this.processer.firstName} ${this.processer.lastName}`,
            date: new Date(),
            content: ['Created Shift'],
          }],
        reserved: this.isAssignedToAll,
        tentative: this.isTentative,
        status: SHIFT_STATUSES.open,
      })
      this.checkTimeConflict(this.selectedEmployee)
      this.checkSubscriptionLimits()
    },
    removeShift(index) {
      this.shifts.splice(index, 1)
      if (this.isAssignedToAll) {
        this.checkTimeConflict(this.selectedEmployee)
      }
    },
    copy(item, index) {
      this.shifts.splice(index + 1, 0, {
        startStr: item.startStr,
        endStr: item.endStr,
        start: item.start,
        end: item.end,
        roleType: item.roleType,
        requirements: item.requirements,
        finalConfirmation: { confirmed: false },
        confirmationRequest: '',
        verificationRequest: '',
        createdBy: this.processer.role,
        updates: [
          {
            state: SHIFT_UPDATE_STATES.created,
            processer: `${this.processer.firstName} ${this.processer.lastName}`,
            date: new Date(),
            content: ['Created Shift'],
          }],
        title: '',
        status: this.isAssignedToAll ? SHIFT_STATUSES.booked : SHIFT_STATUSES.open,
        reserved: this.isAssignedToAll,
        tentative: this.isTentative,
        assignedTo: this.isAssignedToAll ? this.selectedEmployee : '',
      })
      this.checkSubscriptionLimits()
    },
    handleCreate(isDraft, reserved) {
      this.form.isPublished = !isDraft
      this.shifts = this.shifts.map(shift => ({
        ...shift,
        // eslint-disable-next-line no-nested-ternary
        status: shift.assignedTo === ''
          ? isDraft
            ? 'draft'
            : 'open'
          : 'booked',
        isDraft,
        reserved,
      }))
      this.handleSubmit()
    },
    async handleSubmit() {
      this.$refs.rules.validate().then(async isFormValid => { // form validation to see if required fields valid
        /* if (this.user.role === 'customer') { // TODO should be appUser (the user using this hw-admin, not the client)
          this.form.order.emails = this.user.email // in case of order, gig.order.emails = area.email / comes from area obj when open the modal, look in areas collection
        }
        if (this.form.location.abscenceInfo === '') {
          this.form.location.abscenceInfo = 'Akuutit poissaolot ilmoitetaan klo 6.45, arkipyhisin ja vkl klo 7:00'
        }
        if (isFormValid) { // createGig
          const params = {
            form: this.form,
            inputShifts: this.shifts,
            selectedAccount: this.selectedAccount,
            isBoosted: this.isBoosted,
            selectedBoostedOption: this.selectedBoostedOption,
            selectedBoostedValue: this.selectedBoostedValue,
            assignedArea: this.assignedArea,
            status: GIG_STATUSES.open,
            contract: this.contract,
            updateStates: this.updateStates,
          }
          const {shifts, gig} = await gigPublisher.createShiftsAndGig(params)
          if (gig) {
            this.$emit('create-gig', { gig, shifts, isEditing: this.isEditing, processer: `${this.processer.firstName} ${this.processer.lastName}` }) // trigger onGigCreateEdit, 1. update/create gig; 2. refetch gig list
            this.$bvModal.hide('create-gig-modal')
          }else{
            this.$toast(hwToast.error({title: 'not all shifts are valid', text: 'Please check shift times, something went wrong.'}))
          }
        } */

        if (this.user.role === 'customer') { // TODO should be appUser (the user using this hw-admin, not the client)
          this.form.order.emails = this.user.email // in case of order, gig.order.emails = area.email / comes from area obj when open the modal, look in areas collection
        }
        if (this.form.location.abscenceInfo === '') {
          this.form.location.abscenceInfo = 'Akuutit poissaolot ilmoitetaan klo 6.45, arkipyhisin ja vkl klo 7:00'
        }

        if (isFormValid) {
          try {
            const twoDaysForward = new Date()
            twoDaysForward.setDate(twoDaysForward.getDate() + 2)

            if (this.isBoosted) {
              const params = {
                form: this.form,
                inputShifts: this.shifts,
                selectedAccount: this.selectedAccount,
                isBoosted: this.isBoosted,
                selectedBoostedOption: this.selectedBoostedOption,
                selectedBoostedValue: this.selectedBoostedValue,
                assignedArea: this.assignedArea,
                status: GIG_STATUSES.open,
                contract: this.contract,
                updateStates: this.updateStates,
              }
              const {shifts, gig} = await gigPublisher.createShiftsAndGig(params)

              this.$emit('create-gig', { gig, shifts, isEditing: this.isEditing, processer: `${this.processer.firstName} ${this.processer.lastName}`, shouldRefresh: true })
              this.$bvModal.hide('create-gig-modal')

              return
            }

            const shiftsToBoost = this.shifts.filter(shift => shift.start <= twoDaysForward && shift.start >= new Date())
            const regularShifts = this.shifts.filter(shift => shift.start > twoDaysForward || shift.start < new Date())

            const boostedShiftsParams = {
              form: this.form,
              inputShifts: shiftsToBoost,
              selectedAccount: this.selectedAccount,
              isBoosted: true,
              selectedBoostedOption: this.selectedBoostedOption,
              selectedBoostedValue: '',
              assignedArea: this.assignedArea,
              status: GIG_STATUSES.open,
              contract: this.contract,
              updateStates: this.updateStates,
            }
            const regularShiftsParams = {
              form: this.form,
              inputShifts: regularShifts,
              selectedAccount: this.selectedAccount,
              isBoosted: false,
              selectedBoostedOption: this.selectedBoostedOption,
              selectedBoostedValue: '',
              assignedArea: this.assignedArea,
              status: GIG_STATUSES.open,
              contract: this.contract,
              updateStates: this.updateStates,
            }

            if (shiftsToBoost.length > 0) {
              const {shifts, gig} = await gigPublisher.createShiftsAndGig(boostedShiftsParams)
              this.$emit('create-gig', { gig, shifts, isEditing: this.isEditing, processer: `${this.processer.firstName} ${this.processer.lastName}`, shouldRefresh: regularShifts.length < 1 })
            }
            if (regularShifts.length > 0) {
              const {shifts, gig} = await gigPublisher.createShiftsAndGig(regularShiftsParams)
              this.$emit('create-gig', { gig, shifts, isEditing: this.isEditing, processer: `${this.processer.firstName} ${this.processer.lastName}`, shouldRefresh: true })
            }

            this.$bvModal.hide('create-gig-modal')
          } catch (e) {
            console.error(e)
            this.$toast(
              hwToast.error({
                title: 'not all shifts are valid',
                text: 'Please check shift times, something went wrong.'
              })
            )
          }
        }
      })
    },
    onEditRequirements(item) {
      if (this.isEditing) {
        const exist = this.updateStates.find(update => update.id === item.id)
        const index = this.updateStates.indexOf(exist)
        const updateObj = (index !== -1)
          ? this.updateStates[index]
          : {
            state: SHIFT_UPDATE_STATES.created,
            processer: `${this.processer.firstName} ${this.processer.lastName}`,
            date: new Date(),
            content: {},
            id: item.id,
          }
        updateObj.content = { ...updateObj.content, requirements: item.requirements }
        this.updateStates[index > -1 ? index : this.updateStates.length] = updateObj
      }
    },
    onToggleBoosted() {
      const value = !this.isBoosted
      this.isBoosted = value
      this.selectedBoostedOption = SHIFT_BOOST_TYPES.quick
      if (this.isEditing) {
        this.shifts.forEach(shift => {
          const index = this.updateStates.findIndex(item => item.id === shift.id)
          const updateObj = (index !== -1)
            ? this.updateStates[index]
            : {
              state: SHIFT_UPDATE_STATES.updated,
              processer: `${this.processer.firstName} ${this.processer.lastName}`,
              date: new Date(),
              content: {},
              id: shift.id,
            }
          if (value) {
            updateObj.content = { ...updateObj.content, shiftBoost: value, shiftBoostType: this.selectedBoostedOption.value }
          } else {
            updateObj.content = { ...updateObj.content, shiftBoost: value }
          }
          this.updateStates[index > -1 ? index : this.updateStates.length] = updateObj
        })
      }
    },
    onSelectedBoostType(option) {
      if (option) {
        this.selectedBoostedOption = option
      }
      if (option.value === SHIFT_BOOST_TYPES.quick.value) {
        this.selectedBoostedValue = ''
      }
      if (this.isEditing) {
        this.shifts.forEach(shift => {
          const index = this.updateStates.findIndex(item => item.id === shift.id)
          const updateObj = (index !== -1)
            ? this.updateStates[index]
            : {
              state: SHIFT_UPDATE_STATES.updated,
              processer: `${this.processer.firstName} ${this.processer.lastName}`,
              date: new Date(),
              content: {},
              id: shift.id,
            }
          updateObj.content = { ...updateObj.content, shiftBoostType: this.selectedBoostedOption.value }
          this.updateStates[index > -1 ? index : this.updateStates.length] = updateObj
        })
      }
    },
    onAssignToAll() {
      this.isAssignedToAll = !this.isAssignedToAll

      if (!this.isAssignedToAll) {
        this.selectedEmployee = ''
        this.isDoubleBooking = false
      }
    },
    onSelectedEmployee(option) {
      this.selectedEmployee = option.item || ''
      this.checkTimeConflict(option.item)

      if (!this.isDoubleBooking) {
        this.updateShiftAssignedTo()
      }
    },
    updateShiftAssignedTo() {
      this.shifts = this.shifts.map(shift => ({
        ...shift,
        assignedTo: this.selectedEmployee || '',
        status: this.selectedEmployee ? SHIFT_STATUSES.booked : SHIFT_STATUSES.open,
      }))
    },
  },
}
</script>

<style lang="scss">
@import '@hw-ui/scss/vue/libs/vue-flatpicker.scss';
@import '@hw-ui/scss/vue/libs/vue-autosuggest.scss';
.boosted-row {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start
}
.shift-item {
  padding: 0 1rem;
  & > * {
    padding: 0;
  }
  .shift-end, .shift-requirements-col {
    padding-left: 0;
  }
  .shift-requirements {
    padding-left: 1.25rem;
  }
  .shift-actions {
    max-width: 150px;
    margin-left: 35px;
    button {
      height: 37px;
    }
  }
}
.vs__clear {
  margin-right: 0 !important;
}
</style>
